import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtMoney } from "@/components/transport/transportShared";
import { Plus, Trash2, Wheat, Store, IndianRupee, Wallet } from "lucide-react";

export { fmtMoney };

const BADGE = {
  PLACED: "bg-amber-100 text-amber-900",
  CONFIRMED: "bg-blue-100 text-blue-800",
  OUT_FOR_DELIVERY: "bg-indigo-100 text-indigo-800",
  DELIVERED: "bg-emerald-100 text-emerald-800",
  CANCELLED: "bg-red-100 text-red-700",
  REFUNDED: "bg-rose-100 text-rose-800",
  PENDING: "bg-amber-100 text-amber-900",
  APPROVED: "bg-blue-100 text-blue-800",
  PAID: "bg-emerald-100 text-emerald-800",
  UNPAID: "bg-orange-100 text-orange-900",
  ORDERED: "bg-amber-100 text-amber-900",
  RECEIVED: "bg-emerald-100 text-emerald-800",
};

export function FeedBadge({ status }) {
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${BADGE[status] || "bg-muted"}`}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

/** Seller identity — the one visual that keeps both business models distinguishable. */
export function SellerBadge({ model, seller }) {
  const direct = model === "DIRECT";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${direct ? "bg-emerald-100 text-emerald-800" : "bg-violet-100 text-violet-800"}`}>
      {direct ? <Wheat className="w-3 h-3" /> : <Store className="w-3 h-3" />}
      {direct ? "AnimalMela" : seller || "Supplier"}
    </span>
  );
}

export function FeedTable({ cols, children, testId }) {
  return (
    <div className="am-card overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
            {cols.map((c) => <th key={c} className="px-3 py-3 whitespace-nowrap">{c}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function FeedFormDialog({ open, setOpen, title, fields, onSave, busy, wide }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className={`${wide ? "max-w-lg" : "max-w-md"} max-h-[88vh] overflow-y-auto`} data-testid="feed-form-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">{fields}</div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="am-btn-primary" disabled={busy} data-testid="feed-form-save" onClick={onSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const FF = ({ label, children }) => (
  <div><Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</Label><div className="mt-1">{children}</div></div>
);

/* ============ NEW ORDER DIALOG (both models in one cart) ============ */
export function OrderDialog({ products, open, setOpen, onDone }) {
  const [f, setF] = useState({});
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF({}); setLines([{ product_id: "", qty: "" }]); } }, [open]);

  const sellable = products.filter((p) => p.is_active && !p.is_archived);
  const prod = (id) => sellable.find((p) => p.product_id === id);
  const setLine = (i, k, v) => setLines(lines.map((ln, j) => (j === i ? { ...ln, [k]: v } : ln)));
  const subtotal = lines.reduce((s, ln) => {
    const p = prod(ln.product_id);
    return s + (p ? (Number(ln.qty) || 0) * p.selling_price : 0);
  }, 0);
  const total = subtotal + (Number(f.delivery_charge) || 0);

  const save = async () => {
    const items = lines.filter((ln) => ln.product_id && Number(ln.qty) > 0)
      .map((ln) => ({ product_id: ln.product_id, qty: Number(ln.qty) }));
    if (!f.customer_name?.trim()) { toast.error("Customer name is required"); return; }
    if (!items.length) { toast.error("Add at least one product line"); return; }
    setBusy(true);
    try {
      await api.post("/feed/orders", {
        customer_name: f.customer_name, customer_mobile: f.customer_mobile || "",
        delivery_address: f.delivery_address || "", notes: f.notes || "",
        delivery_charge: Number(f.delivery_charge) || 0, items,
      });
      toast.success("Order placed");
      setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Order failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="feed-order-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wheat className="w-5 h-5 text-primary" /> New Feed Order</DialogTitle>
          <DialogDescription>Prices and stock are validated by the server at save time.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <FF label="Customer Name *"><Input className="am-input" data-testid="fo-customer" value={f.customer_name || ""} onChange={(e) => setF({ ...f, customer_name: e.target.value })} /></FF>
          <FF label="Mobile"><Input className="am-input" data-testid="fo-mobile" value={f.customer_mobile || ""} onChange={(e) => setF({ ...f, customer_mobile: e.target.value })} /></FF>
        </div>
        <FF label="Delivery Address"><Input className="am-input" data-testid="fo-address" value={f.delivery_address || ""} onChange={(e) => setF({ ...f, delivery_address: e.target.value })} /></FF>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Items</Label>
          {lines.map((ln, i) => {
            const p = prod(ln.product_id);
            return (
              <div key={i} className="rounded-xl border border-border p-2.5 space-y-1.5" data-testid={`fo-line-${i}`}>
                <div className="flex gap-2 items-center">
                  <Select value={ln.product_id} onValueChange={(v) => setLine(i, "product_id", v)}>
                    <SelectTrigger className="am-input h-9 text-xs flex-1" data-testid={`fo-product-${i}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>
                      {sellable.map((pp) => (
                        <SelectItem key={pp.product_id} value={pp.product_id}>
                          {pp.name} · {fmtMoney(pp.selling_price)}/{pp.unit} · {pp.supply_model === "DIRECT" ? "AnimalMela" : pp.seller_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" min={0} placeholder="Qty" inputMode="decimal" className="am-input h-9 w-24 text-sm"
                    data-testid={`fo-qty-${i}`} value={ln.qty} onChange={(e) => setLine(i, "qty", e.target.value)} />
                  <Button size="icon" variant="ghost" className="shrink-0" onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
                {p && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                    <span className="flex items-center gap-1.5"><SellerBadge model={p.supply_model} seller={p.seller_name} />
                      stock {p.available_stock} {p.unit}</span>
                    <b data-testid={`fo-line-total-${i}`}>{fmtMoney((Number(ln.qty) || 0) * p.selling_price)}</b>
                  </div>
                )}
              </div>
            );
          })}
          <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid="fo-add-line"
            onClick={() => setLines([...lines, { product_id: "", qty: "" }])}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Item
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FF label="Delivery Charge ₹"><Input type="number" min={0} className="am-input" data-testid="fo-delivery"
            value={f.delivery_charge || ""} onChange={(e) => setF({ ...f, delivery_charge: e.target.value })} /></FF>
          <FF label="Notes"><Input className="am-input" value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></FF>
        </div>
        <div className="rounded-xl bg-muted px-4 py-2.5 flex items-center justify-between text-sm font-bold">
          <span>Total (est.)</span><span data-testid="fo-total">{fmtMoney(total)}</span>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="am-btn-primary" disabled={busy} data-testid="fo-save" onClick={save}>Place Order</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ ORDERS PANEL (shared by Admin tab + Helper page) ============ */
const NEXT_STEP = { PLACED: ["CONFIRMED", "Confirm"], CONFIRMED: ["OUT_FOR_DELIVERY", "Dispatch"], OUT_FOR_DELIVERY: ["DELIVERED", "Mark Delivered"] };

export function OrdersPanel({ isAdmin, onMutate }) {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [orderOpen, setOrderOpen] = useState(false);
  const [payFor, setPayFor] = useState(null);
  const [refundFor, setRefundFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    api.get("/feed/orders").then((r) => setOrders(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Failed to load orders"));
    api.get("/feed/products").then((r) => setProducts(r.data)).catch(() => {});
  };
  useEffect(refresh, []);
  // Order mutations move stock — bubble up so sibling tabs (Products) stay fresh.
  const onDone = () => { refresh(); onMutate?.(); };

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Button className="am-btn-primary rounded-full" data-testid="feed-new-order-btn" onClick={() => setOrderOpen(true)}>
        <Plus className="w-4 h-4 mr-1" /> New Order
      </Button>
      <FeedTable testId="feed-orders-table" cols={["Order", "Items", "Total", "Payment", "Status", "Actions"]}>
        {orders.map((o) => {
          const step = NEXT_STEP[o.status];
          return (
            <tr key={o.order_id} className="border-b last:border-0 align-top" data-testid={`feed-order-row-${o.order_id}`}>
              <td className="px-3 py-3 whitespace-nowrap">
                <div className="font-bold">{o.customer_name}</div>
                <div className="text-xs text-muted-foreground font-mono">{o.order_id.slice(0, 8)} · {(o.created_at || "").slice(0, 10)}</div>
              </td>
              <td className="px-3 py-3">
                {(o.items || []).map((ln, i) => (
                  <div key={i} className="text-xs flex items-center gap-1.5 py-0.5 whitespace-nowrap">
                    <SellerBadge model={ln.supply_model} seller={ln.seller_name} />
                    {ln.name} × {ln.qty} {ln.unit} = {fmtMoney(ln.line_total)}
                  </div>
                ))}
              </td>
              <td className="px-3 py-3 font-bold whitespace-nowrap" data-testid={`feed-order-total-${o.order_id}`}>{fmtMoney(o.total)}
                {o.delivery_charge > 0 && <div className="text-[11px] font-normal text-muted-foreground">incl. delivery {fmtMoney(o.delivery_charge)}</div>}
              </td>
              <td className="px-3 py-3"><FeedBadge status={o.payment_status} />
                {o.payment_mode && <div className="text-[11px] text-muted-foreground mt-0.5">{o.payment_mode}</div>}
              </td>
              <td className="px-3 py-3"><FeedBadge status={o.status} /></td>
              <td className="px-3 py-3">
                <div className="flex gap-1.5 justify-end flex-wrap">
                  {step && (
                    <Button size="sm" className="am-btn-primary rounded-full text-xs" disabled={busy}
                      data-testid={`feed-step-${o.order_id}`}
                      onClick={() => act(() => api.post(`/feed/orders/${o.order_id}/status`, { status: step[0] }), step[1])}>
                      {step[1]}
                    </Button>
                  )}
                  {o.payment_status === "UNPAID" && !["CANCELLED", "REFUNDED"].includes(o.status) && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs" disabled={busy}
                      data-testid={`feed-pay-${o.order_id}`} onClick={() => setPayFor(o)}>
                      <IndianRupee className="w-3.5 h-3.5 mr-0.5" /> Record Payment
                    </Button>
                  )}
                  {["PLACED", "CONFIRMED"].includes(o.status) && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs text-destructive" disabled={busy}
                      data-testid={`feed-cancel-${o.order_id}`}
                      onClick={() => act(() => api.post(`/feed/orders/${o.order_id}/cancel`), "Order cancelled · stock restored")}>
                      Cancel
                    </Button>
                  )}
                  {isAdmin && o.status === "DELIVERED" && o.payment_status === "PAID" && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs text-destructive" disabled={busy}
                      data-testid={`feed-refund-${o.order_id}`} onClick={() => setRefundFor(o)}>
                      Refund
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          );
        })}
        {orders.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground">No orders yet.</td></tr>}
      </FeedTable>

      <OrderDialog products={products} open={orderOpen} setOpen={setOrderOpen} onDone={onDone} />
      <PaymentDialog order={payFor} open={!!payFor} setOpen={() => setPayFor(null)} onDone={onDone} />
      {isAdmin && <RefundDialog order={refundFor} open={!!refundFor} setOpen={() => setRefundFor(null)} onDone={onDone} />}
    </div>
  );
}

function PaymentDialog({ order, open, setOpen, onDone }) {
  const [mode, setMode] = useState("cash");
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setMode("cash"); setRef(""); } }, [open]);
  if (!order) return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/feed/orders/${order.order_id}/record-payment`, { payment_mode: mode, transaction_reference: ref || null });
      toast.success("Payment recorded"); setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm" data-testid="feed-pay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="w-5 h-5 text-primary" /> Record Payment</DialogTitle>
          <DialogDescription>{order.customer_name} · {fmtMoney(order.total)}</DialogDescription>
        </DialogHeader>
        <FF label="Payment Mode">
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="am-input" data-testid="feed-pay-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[["cash", "Cash"], ["upi", "UPI"], ["bank_transfer", "Bank Transfer"], ["other", "Other"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </FF>
        <FF label="Transaction Reference (optional)"><Input className="am-input" data-testid="feed-pay-ref" value={ref} onChange={(e) => setRef(e.target.value)} /></FF>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="am-btn-primary" disabled={busy} data-testid="feed-pay-save" onClick={save}>Record {fmtMoney(order.total)}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RefundDialog({ order, open, setOpen, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  if (!order) return null;
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/feed/orders/${order.order_id}/refund`, { reason });
      toast.success("Order refunded · stock restored"); setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-sm" data-testid="feed-refund-dialog">
        <DialogHeader>
          <DialogTitle>Refund Order</DialogTitle>
          <DialogDescription>{order.customer_name} · {fmtMoney(order.total)} — goods return to stock; pending supplier settlements are cancelled.</DialogDescription>
        </DialogHeader>
        <FF label="Reason *"><Textarea rows={2} className="am-input" data-testid="feed-refund-reason" value={reason} onChange={(e) => setReason(e.target.value)} /></FF>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="rounded-full bg-destructive hover:bg-destructive/90 text-white" disabled={!reason.trim() || busy}
            data-testid="feed-refund-save" onClick={save}>Refund {fmtMoney(order.total)}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
